'use client';

import { useCallback, useEffect, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader2 } from 'lucide-react';

import { DateStrip } from '~/(tenant)/book/_components/date-strip';
import { getAllBookingSlotsBatch } from '~/(tenant)/book/_lib/server/server-actions';

import type {
  CoachInfo,
  TrainerTemplate,
} from '../_lib/server/coaches-page.loader';
import type { CoachSlot } from './coaches-list';
import { CoachCard } from './coach-card';

interface CoachProfileProps {
  coach: CoachInfo;
  trainerTemplates: TrainerTemplate[];
  /** Tenant's advance-booking horizon in days; NULL when unconfigured (falls back to 30, matching /book). */
  maxAdvanceBookingDays: number | null;
}

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function CoachProfile({
  coach,
  trainerTemplates,
  maxAdvanceBookingDays,
}: CoachProfileProps) {
  const { t, i18n } = useTranslation('coaches');
  const hasSchedule = trainerTemplates.length > 0;
  // Cap the date strip to the tenant's advance-booking horizon, mirroring /book
  // (booking-form.tsx defaults to 30 days when the tenant hasn't set a limit).
  const daysToShow = maxAdvanceBookingDays ?? 30;
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [slots, setSlots] = useState<CoachSlot[]>([]);
  const [isPending, startTransition] = useTransition();
  const [hasFetched, setHasFetched] = useState(false);

  const fetchSlotsForDate = useCallback(
    async (date: Date) => {
      if (trainerTemplates.length === 0) return;

      const dateStr = formatDateKey(date);

      // Single batched call — see coaches-list.tsx for rationale.
      const templatesById = new Map(trainerTemplates.map((t) => [t.id, t]));
      // try/catch: a transport-level rejection of the single POST (offline
      // device, action route error) must degrade to the empty-slots state,
      // not strand the page in "still loading" — the old per-template calls
      // each carried their own catch, and this one POST now carries them all.
      const batch = await getAllBookingSlotsBatch(
        trainerTemplates.map((t) => t.id),
        dateStr,
        'trainer',
      ).catch(() => ({ success: false as const, error: 'Failed to fetch slots' }));
      const results = batch.success
        ? batch.results.flatMap(({ templateId, result }) => {
            const template = templatesById.get(templateId);
            if (!template) return [];
            return [{ template, slots: result.data ?? [] }];
          })
        : [];

      const coachSlots: CoachSlot[] = [];

      for (const { template, slots: templateSlots } of results) {
        for (const slot of templateSlots) {
          if (!slot.is_available || !slot.available_resources) continue;

          for (const resource of slot.available_resources) {
            if (resource.id !== coach.id) continue;

            coachSlots.push({
              startTime: slot.start_time,
              endTime: slot.end_time,
              isPeak: slot.is_peak ?? false,
              templateId: template.id,
              duration: template.duration,
              price: resource.normal_price,
              sportSlug: template.sportSlug,
            });
          }
        }
      }

      // Deduplicate by startTime (keep shortest duration first)
      const seen = new Set<string>();
      const unique = coachSlots.filter((s) => {
        if (seen.has(s.startTime)) return false;
        seen.add(s.startTime);
        return true;
      });
      unique.sort((a, b) => a.startTime.localeCompare(b.startTime));

      setSlots(unique);
      setHasFetched(true);
    },
    [trainerTemplates, coach.id],
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      startTransition(() => {
        fetchSlotsForDate(date);
      });
    },
    [fetchSlotsForDate],
  );

  useEffect(() => {
    if (trainerTemplates.length === 0) return;
    startTransition(() => {
      fetchSlotsForDate(selectedDate);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No schedule at all — render bio only (no date picker, no slot fetching)
  if (!hasSchedule) {
    return (
      <div className="space-y-6">
        <CoachCard coach={coach} slots={[]} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date selector */}
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          {t('selectDate', 'Select date:')}
        </p>
        <DateStrip
          selected={selectedDate}
          onSelect={handleDateSelect}
          daysToShow={daysToShow}
          language={i18n.language}
        />
      </div>

      {/* Loading state */}
      {isPending && (
        <div
          className="flex items-center justify-center py-8"
          role="status"
          aria-label={t('loading', 'Loading available slots...')}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Coach card with slots */}
      {!isPending && (
        <>
          <CoachCard
            coach={coach}
            slots={slots}
            selectedDate={selectedDate}
            locationSlug={coach.locationSlug}
          />

          {hasFetched && slots.length === 0 && (
            <div className="py-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t(
                  'noSlotsForDate',
                  'No available slots on this date.',
                )}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
